import { memo } from 'react'

import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { IReport, REPORT_REASON, REPORT_STATUS } from '~/types/report'

import { ReportCountBadge, ReportReasonBadge, ReportStatusBadge } from '~/components/Badges'
import ReviewedByCellRenderer from '~/components/CellRenderers/ReviewedByCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import UserCard from '~/components/UserCard'
import { FASTLY_PATH } from '~/const/asset'
import { REPORT_TYPE } from '~/types/report'
import { capitalize } from '~/utils/string'
import { dateColDef, textColDef } from '~/utils/table'

export const COLUMNS = {
  APP_KEY: 'app_key',
  CREATED_AT: 'created_at',
  CREATED_BY: 'profile_id',
  DETAILS: 'details',
  ENTITY: 'entity',
  REASON: 'reason_types',
  REPORTS_COUNT: 'reports_count',
  REVIEWER: 'reviewed_by',
  REVIEW_AT: 'reviewed_at',
  STATUS: 'status',
  TYPE: 'reported_entity_type',
}

const ReportsStatusCellRenderer = ({ value }: ICellRendererParams<IReport, IReport['status']>): ReactElement => {
  return (
    <div className="reports-badge-cell-renderer">
      {value ? <ReportStatusBadge value={value as REPORT_STATUS} /> : null}
    </div>
  )
}

const ReportsReasonCellRenderer = ({ value }: ICellRendererParams<IReport, IReport['reason_types']>): ReactElement => {
  return (
    <div className="reports-badge-cell-renderer">
      {value && value.length ? <ReportReasonBadge value={value[0] as REPORT_REASON} /> : null}
    </div>
  )
}

const ReportsCountCellRenderer = ({ value }: ICellRendererParams<IReport, IReport['reports_count']>): ReactElement => {
  return <div className="reports-badge-cell-renderer">{value ? <ReportCountBadge value={value} /> : null}</div>
}

const ReportEntityCellRenderer = memo(({ value }: ICellRendererParams<IReport>): ReactElement => {
  const {
    data: { content, display_name, file_id, profile_picture_file_id },
    reported_entity_type,
  } = value

  switch (reported_entity_type) {
    case REPORT_TYPE.POST:
      return (
        <div className="reports-post">
          {file_id ? (
            <img alt="Reported post picture" src={`${FASTLY_PATH}${file_id}` || ''} />
          ) : (
            <div>No picture provided</div>
          )}
        </div>
      )
    case REPORT_TYPE.PROFILE:
      return (
        <div className="reports-profile">
          <UserCard name={capitalize(display_name)} picture={`${FASTLY_PATH}${profile_picture_file_id}`} size="small" />
        </div>
      )
    case REPORT_TYPE.COMMENT:
      return (
        <div className="reports-comment">
          <div>{content}</div>
        </div>
      )
    default:
      return <div>No entity</div>
  }
})

ReportEntityCellRenderer.displayName = 'ReportEntityCellRenderer'

const columns: ColDef[] = [
  dateColDef({
    field: COLUMNS.CREATED_AT,
    headerName: 'Date',
    menuTabs: [],
    suppressHeaderMenuButton: true,
    valueGetter: ({ data }: ValueGetterParams<IReport>): number | null =>
      (data && data.data.created_at * 1000000) || null,
  }),
  {
    cellRenderer: ReportEntityCellRenderer,
    field: COLUMNS.ENTITY,
    headerName: 'Reported Entity',
    menuTabs: [],
    minWidth: 200,
    sortable: false,
    valueGetter: ({ data }: ValueGetterParams<IReport>): IReport | undefined => data,
  },
  {
    cellRenderer: ReportsCountCellRenderer,
    field: COLUMNS.REPORTS_COUNT,
    headerName: 'Reported count',
    menuTabs: [],
    minWidth: 200,
  },
  {
    cellRenderer: ReportsStatusCellRenderer,
    field: COLUMNS.STATUS,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      trimInput: true,
    },
    headerName: 'Status',
    menuTabs: ['filterMenuTab'],
    minWidth: 200,
    sortable: false,
  },
  textColDef(TextCellRenderer, {
    field: COLUMNS.TYPE,
    filter: 'agSetColumnFilter',
    headerName: 'Type',
    sortable: false,
    valueGetter: ({ data }: ValueGetterParams<IReport>): string =>
      data?.reported_entity_type ? capitalize(data.reported_entity_type) : '',
  }),
  {
    cellRenderer: ReportsReasonCellRenderer,
    field: COLUMNS.REASON,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      trimInput: true,
    },
    headerName: 'Reasons',
    menuTabs: ['filterMenuTab'],
    minWidth: 200,
    sortable: false,
  },
  dateColDef({
    field: COLUMNS.REVIEW_AT,
    headerName: 'Review date',
    menuTabs: [],
    suppressHeaderMenuButton: true,
    valueGetter: ({ data }: ValueGetterParams<IReport>): number | null => (data && data.reviewed_at * 1000000) || null,
  }),
  {
    cellRenderer: ReviewedByCellRenderer,
    field: COLUMNS.REVIEWER,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      trimInput: true,
    },
    headerName: 'Reviewed by',
    menuTabs: ['filterMenuTab'],
    minWidth: 200,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
]
export default columns
